<template>
  <div class="flight-search-form">
    <!-- Trip Type and Cabin Class Selection -->
    <div class="columns">
      <div class="column">
        <b-field has-addons>
          <b-radio-button v-model="tripType" native-value="oneway" type="is-primary" size="is-small">
            <b-icon icon="arrow-right" size="is-small"></b-icon>
            <span>One Way</span>
          </b-radio-button>
          <b-radio-button v-model="tripType" native-value="roundtrip" type="is-primary" size="is-small">
            <b-icon icon="swap-horizontal" size="is-small"></b-icon>
            <span>Round Trip</span>
          </b-radio-button>
          <b-radio-button v-model="tripType" native-value="multicity" type="is-primary" size="is-small">
            <b-icon icon="map-marker-multiple" size="is-small"></b-icon>
            <span>Multi-City</span>
          </b-radio-button>
        </b-field>
      </div>
      <div class="column">
        <b-field>
          <b-dropdown v-model="isCabinClassDropdownOpen" :mobile-modal="false" aria-role="list">
            <template #trigger>
              <b-button
                type="is-text"
                size="is-small"
                icon-right="chevron-down"
                expanded
              >
                {{ getCabinClassName(cabinClass) }}
              </b-button>
            </template>

            <b-dropdown-item
              v-for="option in cabinClasses"
              :key="option.value"
              @click="cabinClass = option.value"
              aria-role="listitem"
            >
              {{ option.name }}
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>
    </div>

    <!-- Location Fields with Airport Search (Round Trip / One Way) -->
    <div v-if="tripType !== 'multicity'" class="columns">
      <div class="column">
        <b-field label="" :type="errors.from ? 'is-danger' : ''" :message="errors.from">
          <LocationSearch
            icon="airplane-takeoff"
            :value="fromLocation"
            @locUpdate="setFromLocation"
            @blur="onFromLocationBlur"
            placeholder="From (e.g. LAX, BOS)"
            :offerHomeAirport="true"
          />
        </b-field>
      </div>
      <div class="column">
        <b-field label="" :type="errors.to ? 'is-danger' : ''" :message="errors.to">
          <LocationSearch
            icon="airplane-landing"
            :value="toLocation"
            @locUpdate="setToLocation"
            @blur="onToLocationBlur"
            placeholder="To (e.g. LAX, BOS)"
            :offerHomeAirport="true"
          />
        </b-field>
      </div>
    </div>

    <!-- Multi-City Location Fields -->
    <div v-if="tripType === 'multicity'" class="multi-city-container">
      <div v-for="(segment, index) in multiCitySegments" :key="index" class="multi-city-segment mb-4">
        <div class="columns">
          <div class="column">
            <b-field :label="`Flight ${index + 1} - From`" :type="errors[`multiFrom${index}`] ? 'is-danger' : ''" :message="errors[`multiFrom${index}`]">
              <LocationSearch
                icon="airplane-takeoff"
                :value="segment.fromLocation"
                @locUpdate="setMultiFromLocation(index, $event)"
                @blur="onMultiFromBlur(index)"
                placeholder="From (e.g. LAX, BOS)"
                :offerHomeAirport="true"
              />
            </b-field>
          </div>
          <div class="column">
            <b-field :label="`Flight ${index + 1} - To`" :type="errors[`multiTo${index}`] ? 'is-danger' : ''" :message="errors[`multiTo${index}`]">
              <LocationSearch
                icon="airplane-landing"
                :value="segment.toLocation"
                @locUpdate="setMultiToLocation(index, $event)"
                @blur="onMultiToBlur(index)"
                placeholder="To (e.g. LAX, BOS)"
                :offerHomeAirport="true"
              />
            </b-field>
          </div>
          <div class="column">
            <b-field :label="`Flight ${index + 1} - Date`" :type="errors[`multiDate${index}`] ? 'is-danger' : ''" :message="errors[`multiDate${index}`]">
              <b-datepicker
                v-model="segment.departureDate"
                placeholder="Departure date"
                icon="calendar"
                :min-date="getMinDateForSegment(index)"
                @input="onMultiDateChange(index)"
                expanded
              />
            </b-field>
            <b-field>
              <b-dropdown v-model="segment.isTimeDropdownOpen" :mobile-modal="false" aria-role="list">
                <template #trigger>
                  <b-button
                    type="is-text"
                    size="is-small"
                    icon-right="chevron-down"
                    expanded
                  >
                    {{ segment.timePreference }}
                  </b-button>
                </template>

                <b-dropdown-item
                  v-for="option in times"
                  :key="option.format"
                  @click="segment.timePreference = option.format"
                  aria-role="listitem"
                >
                  {{ option.format }}
                </b-dropdown-item>
              </b-dropdown>
            </b-field>
          </div>
          <div class="column is-narrow" v-if="multiCitySegments.length > 2">
            <b-field label=" ">
              <b-button
                type="is-danger"
                size="is-small"
                @click="removeMultiCitySegment(index)"
                class="mt-4"
              >
                <b-icon icon="times" pack="fas" size="is-small"></b-icon>
              </b-button>
            </b-field>
          </div>
        </div>
      </div>
      <div class="has-text-centered mb-4">
        <b-button
          type="is-light"
          size="is-small"
          icon-left="plus"
          @click="addMultiCitySegment"
          v-if="multiCitySegments.length < 6"
        >
          Add Another Flight
        </b-button>
      </div>
    </div>

    <!-- Date and Time Fields (Round Trip / One Way) -->
    <div v-if="tripType !== 'multicity'" class="columns">
      <!-- Departure Date and Time -->
      <div class="column">
        <b-field class="mb-0" label="" :type="errors.departureDate ? 'is-danger' : ''" :message="errors.departureDate">
          <DateSelector
            segmentType="flight"
            :minDate="minDate"
            :focusedDate="minDate"
            :dateIndex="0"
            v-model="departureDate"
            placeholder="Departure date"
            @input="onDepartureDateChange"
          />
        </b-field>
        <b-field>
          <b-dropdown v-model="isDepartureTimeDropdownOpen" :mobile-modal="false" aria-role="list">
            <template #trigger>
              <b-button
                type="is-text"
                size="is-small"
                icon-right="chevron-down"
                expanded
              >
                {{ departureTimePreference }}
              </b-button>
            </template>

            <b-dropdown-item
              v-for="option in times"
              :key="option.format"
              @click="departureTimePreference = option.format"
              aria-role="listitem"
            >
              {{ option.format }}
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>

      <!-- Return Date and Time (Round Trip Only) -->
      <div class="column" v-if="tripType === 'roundtrip'">
        <b-field class="mb-0" label="" :type="errors.returnDate ? 'is-danger' : ''" :message="errors.returnDate">
          <DateSelector
            segmentType="flight"
            :minDate="departureDate || minDate"
            :focusedDate="departureDate || minDate"
            :dateIndex="1"
            v-model="returnDate"
            placeholder="Return date"
            @input="onReturnDateChange"
          />
        </b-field>
        <b-field >
          <b-dropdown v-model="isReturnTimeDropdownOpen" :mobile-modal="false" aria-role="list">
            <template #trigger>
              <b-button
                type="is-text"
                size="is-small"
                icon-right="chevron-down"
                expanded
              >
                {{ returnTimePreference }}
              </b-button>
            </template>

            <b-dropdown-item
              v-for="option in times"
              :key="option.format"
              @click="returnTimePreference = option.format"
              aria-role="listitem"
            >
              {{ option.format }}
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>
    </div>

    <!-- Search Button -->
    <div class="field mt-4">
      <b-button 
        type="is-primary" 
        expanded 
        @click="handleSearch"
        :loading="searching"
        :disabled="!isFormValid"
      >
        <b-icon icon="magnify" class="mr-2"></b-icon>
        Search Flights
      </b-button>
    </div>


  </div>
</template>

<script>
import { mapState } from 'vuex'
import DateSelector from '../DateComponents/DateSelector'
import LocationSearch from '../EDLocation/EDLocationSearch'

export default {
  name: 'FlightSearchForm',
  components: {
    DateSelector,
    LocationSearch
  },
  data() {
    return {
      tripType: 'roundtrip',
      fromLocation: null,
      toLocation: null,
      departureDate: null,
      returnDate: null,
      departureTimePreference: 'Anytime',
      returnTimePreference: 'Anytime',
      cabinClass: 'Y',
      searching: false,
      errors: {},
      touchedFields: {},
      hasAttemptedSubmit: false,
      isComponentReady: false,
      isDepartureTimeDropdownOpen: false,
      isReturnTimeDropdownOpen: false,
      isCabinClassDropdownOpen: false,
      times: [
        { mil: 0, format: "Anytime" },
        { mil: 8, format: "Morning" },
        { mil: 13, format: "Afternoon" },
        { mil: 18, format: "Evening" }
      ],
      cabinClasses: [
        { value: 'Y', name: 'Economy' },
        { value: 'W', name: 'Premium Economy' },
        { value: 'C', name: 'Business' },
        { value: 'F', name: 'First Class' }
      ],
      multiCitySegments: [
        {
          fromLocation: null,
          toLocation: null,
          departureDate: null,
          timePreference: 'Anytime',
          isTimeDropdownOpen: false
        },
        {
          fromLocation: null,
          toLocation: null,
          departureDate: null,
          timePreference: 'Anytime',
          isTimeDropdownOpen: false
        }
      ]
    }
  },
  computed: {
    ...mapState(['bookTravel']),
    minDate() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    },
    homeAirport() {
      const profile = this.$store.getters['travellersAndTrips/getCurrentTravellerProfile']
      return profile?.airPreferences?.homeAirport || null
    },
    airports() {
      return this.$store.state.bookTravel?.airports || []
    },
    isFormValid() {
      if (this.tripType === 'multicity') {
        return this.multiCitySegments.every(segment =>
          segment.fromLocation && segment.toLocation && segment.departureDate
        ) && Object.keys(this.errors).length === 0
      }

      return this.fromLocation &&
             this.toLocation &&
             this.departureDate &&
             (this.tripType === 'oneway' || this.returnDate) &&
             Object.keys(this.errors).length === 0
    },
    storedFlightFormData() {
      return this.$store.state.bookTravel?.flightFormData || null
    }
  },
  methods: {
    setFromLocation(location) {
      console.log('FlightSearchForm - setFromLocation:', location)
      this.fromLocation = location
      // Only mark as touched and validate if component is ready for user interaction
      if (this.isComponentReady) {
        this.$set(this.touchedFields, 'from', true)
        this.validateFrom()
      }
    },

    setToLocation(location) {
      console.log('FlightSearchForm - setToLocation:', location)
      this.toLocation = location
      // Only mark as touched and validate if component is ready for user interaction
      if (this.isComponentReady) {
        this.$set(this.touchedFields, 'to', true)
        this.validateTo()
      }
    },

    onFromLocationBlur() {
      if (this.isComponentReady) {
        this.$set(this.touchedFields, 'from', true)
        this.validateFrom()
      }
    },

    onToLocationBlur() {
      if (this.isComponentReady) {
        this.$set(this.touchedFields, 'to', true)
        this.validateTo()
      }
    },

    onDepartureDateChange() {
      if (this.isComponentReady) {
        this.$set(this.touchedFields, 'departureDate', true)
        this.validateDepartureDate()

        // For round trip flights, set return date to departure date if return date is not set
        if (this.tripType === 'roundtrip' && this.departureDate && !this.returnDate) {
          this.returnDate = new Date(this.departureDate)
        }
      }
    },

    onReturnDateChange() {
      if (this.isComponentReady) {
        this.$set(this.touchedFields, 'returnDate', true)
        this.validateReturnDate()
      }
    },

    onMultiDateChange(segmentIndex) {
      if (this.isComponentReady) {
        this.$set(this.touchedFields, `multiDate${segmentIndex}`, true)
        this.validateMultiDate(segmentIndex)
      }
    },

    setMultiFromLocation(segmentIndex, location) {
      console.log('FlightSearchForm - setMultiFromLocation:', segmentIndex, location)
      this.multiCitySegments[segmentIndex].fromLocation = location
      // Only mark as touched and validate if component is ready for user interaction
      // and if the location is actually set (not empty string from component initialization)
      if (this.isComponentReady && location && location !== '') {
        this.$set(this.touchedFields, `multiFrom${segmentIndex}`, true)
        this.validateMultiFrom(segmentIndex)
      }
    },

    setMultiToLocation(segmentIndex, location) {
      console.log('FlightSearchForm - setMultiToLocation:', segmentIndex, location)
      this.multiCitySegments[segmentIndex].toLocation = location
      // Only mark as touched and validate if component is ready for user interaction
      // and if the location is actually set (not empty string from component initialization)
      if (this.isComponentReady && location && location !== '') {
        this.$set(this.touchedFields, `multiTo${segmentIndex}`, true)
        this.validateMultiTo(segmentIndex)
      }
    },

    onMultiFromBlur(segmentIndex) {
      if (this.isComponentReady) {
        this.$set(this.touchedFields, `multiFrom${segmentIndex}`, true)
        this.validateMultiFrom(segmentIndex)
      }
    },

    onMultiToBlur(segmentIndex) {
      if (this.isComponentReady) {
        this.$set(this.touchedFields, `multiTo${segmentIndex}`, true)
        this.validateMultiTo(segmentIndex)
      }
    },



    validateFrom(forceShow = false) {
      const shouldShowError = forceShow || this.hasAttemptedSubmit || (this.touchedFields.from && this.isComponentReady)
      if (!this.fromLocation && shouldShowError) {
        this.$set(this.errors, 'from', 'Please select a departure airport')
      } else {
        this.$delete(this.errors, 'from')
      }
    },
    validateTo(forceShow = false) {
      const shouldShowError = forceShow || this.hasAttemptedSubmit || (this.touchedFields.to && this.isComponentReady)
      if (!this.toLocation && shouldShowError) {
        this.$set(this.errors, 'to', 'Please select a destination airport')
      } else {
        this.$delete(this.errors, 'to')
      }
    },
    validateDepartureDate(forceShow = false) {
      const shouldShowError = forceShow || this.hasAttemptedSubmit || (this.touchedFields.departureDate && this.isComponentReady)
      if (!this.departureDate && shouldShowError) {
        this.$set(this.errors, 'departureDate', 'Please select a departure date')
      } else if (this.departureDate && shouldShowError) {
        // Normalize dates for comparison by setting time to start of day
        const selectedDate = new Date(this.departureDate)
        selectedDate.setHours(0, 0, 0, 0)
        const minDate = new Date(this.minDate)
        minDate.setHours(0, 0, 0, 0)

        if (selectedDate < minDate) {
          this.$set(this.errors, 'departureDate', 'Please select a future date')
        } else {
          this.$delete(this.errors, 'departureDate')
        }
      } else {
        this.$delete(this.errors, 'departureDate')
      }
    },
    validateReturnDate(forceShow = false) {
      const shouldShowError = forceShow || this.hasAttemptedSubmit || (this.touchedFields.returnDate && this.isComponentReady)
      if (this.tripType === 'roundtrip') {
        if (!this.returnDate && shouldShowError) {
          this.$set(this.errors, 'returnDate', 'Please select a return date')
        } else if (this.returnDate < this.departureDate && shouldShowError) {
          this.$set(this.errors, 'returnDate', 'Return date must be after departure date')
        } else {
          this.$delete(this.errors, 'returnDate')
        }
      }
    },


    // Multi-city validation methods
    validateMultiFrom(segmentIndex, forceShow = false) {
      const errorKey = `multiFrom${segmentIndex}`
      const touchedKey = `multiFrom${segmentIndex}`
      const shouldShowError = forceShow || this.hasAttemptedSubmit || (this.touchedFields[touchedKey] && this.isComponentReady)
      if (!this.multiCitySegments[segmentIndex].fromLocation && shouldShowError) {
        this.$set(this.errors, errorKey, 'Please select a departure airport')
      } else {
        this.$delete(this.errors, errorKey)
      }
    },

    validateMultiTo(segmentIndex, forceShow = false) {
      const errorKey = `multiTo${segmentIndex}`
      const touchedKey = `multiTo${segmentIndex}`
      const shouldShowError = forceShow || this.hasAttemptedSubmit || (this.touchedFields[touchedKey] && this.isComponentReady)
      if (!this.multiCitySegments[segmentIndex].toLocation && shouldShowError) {
        this.$set(this.errors, errorKey, 'Please select a destination airport')
      } else {
        this.$delete(this.errors, errorKey)
      }
    },

    validateMultiDate(segmentIndex, forceShow = false) {
      const errorKey = `multiDate${segmentIndex}`
      const touchedKey = `multiDate${segmentIndex}`
      const shouldShowError = forceShow || this.hasAttemptedSubmit || (this.touchedFields[touchedKey] && this.isComponentReady)
      const segment = this.multiCitySegments[segmentIndex]
      if (!segment.departureDate && shouldShowError) {
        this.$set(this.errors, errorKey, 'Please select a departure date')
      } else if (segment.departureDate && shouldShowError) {
        // Normalize dates for comparison by setting time to start of day
        const selectedDate = new Date(segment.departureDate)
        selectedDate.setHours(0, 0, 0, 0)
        const minDate = new Date(this.getMinDateForSegment(segmentIndex))
        minDate.setHours(0, 0, 0, 0)

        if (selectedDate < minDate) {
          this.$set(this.errors, errorKey, 'Please select a future date')
        } else {
          this.$delete(this.errors, errorKey)
        }
      } else {
        this.$delete(this.errors, errorKey)
      }
    },

    // Multi-city segment management
    addMultiCitySegment() {
      this.multiCitySegments.push({
        fromLocation: null,
        toLocation: null,
        departureDate: null,
        timePreference: 'Anytime',
        isTimeDropdownOpen: false
      })
    },

    removeMultiCitySegment(index) {
      if (this.multiCitySegments.length > 2) {
        this.multiCitySegments.splice(index, 1)
        // Clean up validation errors for removed segment
        this.$delete(this.errors, `multiFrom${index}`)
        this.$delete(this.errors, `multiTo${index}`)
        this.$delete(this.errors, `multiDate${index}`)
      }
    },

    getMinDateForSegment(index) {
      if (index === 0) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return today
      }
      const previousSegment = this.multiCitySegments[index - 1]
      if (previousSegment.departureDate) {
        return previousSegment.departureDate
      }
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    },
    swapLocations() {
      const tempLocation = this.fromLocation
      const tempInput = this.fromLocationInput

      this.fromLocation = this.toLocation
      this.fromLocationInput = this.toLocationInput
      this.toLocation = tempLocation
      this.toLocationInput = tempInput

      // Revalidate after swap (only if component is ready)
      if (this.isComponentReady) {
        this.validateFrom()
        this.validateTo()
      }
    },

    handleSearch() {
      // Mark that a submit attempt has been made
      this.hasAttemptedSubmit = true

      if (this.tripType === 'multicity') {
        // Validate multi-city fields with forceShow = true
        this.multiCitySegments.forEach((_, index) => {
          this.validateMultiFrom(index, true)
          this.validateMultiTo(index, true)
          this.validateMultiDate(index, true)
        })
      } else {
        // Validate regular fields with forceShow = true
        this.validateFrom(true)
        this.validateTo(true)
        this.validateDepartureDate(true)
        this.validateReturnDate(true)
      }

      if (this.isFormValid) {
        this.searching = true

        // Set default traveller settings for business travel
        this.$store.commit('travellersAndTrips/EDIT_TRAVELLERS', {
          tripType: 'business',
          bookFor: 'user',
          set: true
        })

        let searchData
        if (this.tripType === 'multicity') {
          searchData = {
            tripType: this.tripType,
            multiCitySegments: this.multiCitySegments.map(segment => ({
              from: segment.fromLocation,
              to: segment.toLocation,
              departureDate: segment.departureDate,
              timePreference: segment.timePreference
            })),
            cabinClass: this.cabinClass
          }

        } else {
          searchData = {
            tripType: this.tripType,
            from: this.fromLocation,
            to: this.toLocation,
            departureDate: this.departureDate,
            returnDate: this.returnDate,
            cabinClass: this.cabinClass,
            departureTimePreference: this.departureTimePreference,
            returnTimePreference: this.returnTimePreference
          }
        }

        console.log('FlightSearchForm - emitting search data:', JSON.stringify(searchData, null, 2))
        console.log('FlightSearchForm - from location details:', {
          subType: searchData.from?.subType,
          cityCode: searchData.from?.cityCode,
          code: searchData.from?.code,
          city: searchData.from?.city
        })
        console.log('FlightSearchForm - to location details:', {
          subType: searchData.to?.subType,
          cityCode: searchData.to?.cityCode,
          code: searchData.to?.code,
          city: searchData.to?.city
        })
        this.$emit('search', searchData)

        // Reset searching state after a delay
        setTimeout(() => {
          this.searching = false
        }, 1000)
      }
    },

    getCabinClassName(value) {
      const cabin = this.cabinClasses.find(c => c.value === value)
      return cabin ? cabin.name : 'Economy'
    },

    initializeFromStoredData() {
      if (this.storedFlightFormData) {
        const data = this.storedFlightFormData

        // Set trip type
        if (data.tripVal !== undefined) {
          this.tripType = data.tripVal === 0 ? 'oneway' : 'roundtrip'
        }

        // Set cabin class
        if (data.cabinClass) {
          this.cabinClass = data.cabinClass
        }

        // Set from location
        if (data.loc1) {
          this.fromLocation = data.loc1
        }

        // Set to location
        if (data.loc2) {
          this.toLocation = data.loc2
        }

        // Set departure date
        if (data.startDate?.date) {
          this.departureDate = new Date(data.startDate.date)
        }

        // Set return date
        if (data.endDate?.date && this.tripType === 'roundtrip') {
          this.returnDate = new Date(data.endDate.date)
        }

        // Set time preferences
        if (data.startDate?.advanced?.tod) {
          this.departureTimePreference = data.startDate.advanced.tod
        }

        if (data.endDate?.advanced?.tod) {
          this.returnTimePreference = data.endDate.advanced.tod
        }

        // Clear the stored data after using it
        this.$store.commit('bookTravel/CLEAR_FLIGHT_FORM_DATA')
      }
    }
  },

  async mounted() {
    // Load airports if not already loaded
    if (!this.airports || this.airports.length === 0) {
      try {
        const result = await this.$app.huddleLabsServerCall("resources/airports?mode=advanced")
        this.$store.dispatch("bookTravel/airports", result.data)
      } catch (error) {
        console.error('Error loading airports:', error)
      }
    }

    this.initializeFromStoredData()
    // Set component as ready for user interaction after next tick
    this.$nextTick(() => {
      this.isComponentReady = true
    })
  }
}
</script>

<style scoped>
.swap-button-container {
  display: flex;
  align-items: flex-end;
  height: 100%;
  padding-bottom: 0.375rem;
}

.swap-button {
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
}

/* Home airport shortcut styling */
.home-airport-shortcut {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.home-airport-shortcut a {
  color: #a4393a;
  cursor: pointer;
  text-decoration: none;
}

.home-airport-shortcut a:hover {
  text-decoration: underline;
}

/* Multi-city styling */
.multi-city-container {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  padding: 1rem;
  background-color: #fafafa;
}

.multi-city-segment {
  background-color: white;
  border-radius: 4px;
  padding: 1rem;
  border: 1px solid #e8e8e8;
}

.multi-city-segment:not(:last-child) {
  margin-bottom: 1rem;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .columns {
    margin: 0;
  }

  .column {
    padding: 0.5rem;
  }

  .swap-button-container {
    padding-bottom: 0.25rem;
  }

  .swap-button {
    width: 2rem;
    height: 2rem;
  }

  .multi-city-container {
    padding: 0.5rem;
  }

  .multi-city-segment {
    padding: 0.5rem;
  }

  /* Keep date and time fields in columns on mobile for flight search */
  .flight-search-form .columns {
    display: flex !important;
    flex-wrap: nowrap;
    margin: 0 -0.25rem;
  }

  .flight-search-form .columns .column {
    flex: 1;
    min-width: 0;
    padding: 0.25rem;
  }

  /* Ensure location fields still stack properly */
  .flight-search-form .columns:first-of-type,
  .flight-search-form .columns:nth-of-type(2) {
    flex-wrap: wrap;
  }

  .flight-search-form .columns:first-of-type .column,
  .flight-search-form .columns:nth-of-type(2) .column {
    min-width: 280px;
  }
}
</style>
